import ExportarCsv from '@/global/components/DataGrid/components/CustomGridToolBar/ExportarCsv'
import { GridMenuButton } from '@/global/components/DataGrid/components/CustomGridToolBar/MenuComponents'
import { GridSelectionModel } from '@/global/components/DataGrid/utils/types'
import { useFilterLocaisCentroStore } from '@/global/components/Selects/filter-select'
import { useUrlState } from '@/hooks'
import localeText from '@/locales/datagrid'
import { CalculoEspelho, espelhoPonto } from '@/service/api'
import { PaginatedResponse } from '@/service/api/types'
import { useStore } from '@/store'
import BaixarCalculoButton from '@/templates/ManutencaoDoPonto/CalculoPonto/BaixarCalculoButton'
import CalcularEmpresa from '@/templates/ManutencaoDoPonto/CalculoPonto/calcular'
import columns from '@/templates/ManutencaoDoPonto/CalculoPonto/columns'
import SelectCompetencia, {
    useCompetencia,
} from 'global/components/Selects/SelectCompetencia'
import ContentGuide from 'global/guides/ContentGuide'
import { UploadIcon } from 'lucide-react'
import React, { useEffect } from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'
import OverlayApresentacao, {
    IPasso,
} from 'templates/Apresentacao/OverlayApresentacao'

const isMobilePresentation = window.innerWidth <= 1024 // lg

const passos: IPasso[] = [
    {
        title: 'Calcule o ponto',
        description: `Voce pode calcular o ponto de todos os funcionários da empresa
        de uma só vez clicando no botão "Calcular".`,
        getHighlightElement: () =>
            document.querySelector('[data-testid="calcular-empresa"]')!,
        parentElements: isMobilePresentation
            ? [
                  () =>
                      document.querySelector(
                          '[data-testid="botao_opcoes_datatable"]',
                      )!,
              ]
            : undefined,
    },
    {
        title: 'Exportação',
        description: `Exporte os PDFs dos funcionários em um único arquivo ou ZIP. Selecione os funcionários na tabela e clique em "Exportar".`,
        parentElements: isMobilePresentation
            ? [
                  () =>
                      document.querySelector(
                          '[data-testid="botao_opcoes_datatable"]',
                      )!,
                  () =>
                      document.querySelector(
                          '[data-testid="botao_exportar-calculo"]',
                      )!,
              ]
            : [
                  () =>
                      document.querySelector(
                          '[data-testid="botao_exportar-calculo"]',
                      )!,
              ],
        getHighlightElement: () =>
            document.getElementsByClassName('MuiList-root')[0] as any,
    },
]

export default function CalculoPonto() {
    const [competencia, setCompetencia] = useCompetencia()
    const empresaId = useStore(state => state.companyId)
    const costCenterId = useStore(state => state.costCenterId)
    const locaisIds = useStore(state => state.locaisIds)

    const [
        { page, perPage, selectAll: selectAllRaw, excludeIds: excludeIdsRaw },
        setUrlState,
    ] = useUrlState({
        page: 1,
        perPage: 25,
        selectAll: '' as string,
        excludeIds: '' as string,
    })

    const isSelectAll = selectAllRaw === 'true'
    const selectedIds =
        !isSelectAll && selectAllRaw ? selectAllRaw.split(',').map(Number) : []
    const excludeIds = excludeIdsRaw ? excludeIdsRaw.split(',').map(Number) : []

    useEffect(() => {
        setUrlState({ selectAll: '', excludeIds: '' })
    }, [empresaId, competencia, costCenterId, locaisIds])

    const { data, isLoading } = espelhoPonto.useFetch<
        PaginatedResponse<CalculoEspelho>
    >({
        keepPreviousData: true,
        queryParams: {
            competencia,
            empresaId,
            page,
            perPage,
        } as any,
    })

    const useFilter = useFilterLocaisCentroStore()
    const rows = (data?.data ?? []).filter(useFilter)

    const selectionModel = isSelectAll
        ? rows.map(r => r.id).filter(id => !excludeIds.includes(id))
        : selectedIds

    const selectedFuncionarioIds = selectionModel.reduce<number[]>(
        (acc, id) => {
            const row = rows.find(it => it.id === id)

            if (!row) return acc

            acc.push(row.funcionarioId)
            return acc
        },
        [],
    )

    const handleSelectionChange = (
        valueOrUpdater: React.SetStateAction<GridSelectionModel>,
    ) => {
        const newIds =
            typeof valueOrUpdater === 'function'
                ? valueOrUpdater(selectionModel)
                : valueOrUpdater

        if (isSelectAll) {
            const currentPageIds = rows.map(r => r.id)

            // Se desmarcou todos da página de uma vez (header checkbox), sai do selectAll
            if (currentPageIds.length > 0 && newIds.length === 0) {
                setUrlState({ selectAll: '', excludeIds: '' })
                return
            }

            const deselected = currentPageIds.filter(id => !newIds.includes(id))
            const reselected = currentPageIds.filter(id => newIds.includes(id))

            const nextExclude = [
                ...excludeIds.filter(id => !reselected.includes(id)),
                ...deselected.filter(id => !excludeIds.includes(id)),
            ]
            setUrlState({
                excludeIds: nextExclude.length > 0 ? nextExclude.join(',') : '',
            })
        } else {
            const allPageSelected =
                rows.length > 0 && rows.every(r => newIds.includes(r.id))
            if (
                allPageSelected &&
                rows.length === newIds.length &&
                newIds.length > 0
            ) {
                setUrlState({ selectAll: 'true', excludeIds: '' })
            } else {
                setUrlState({
                    selectAll: newIds.length > 0 ? newIds.join(',') : '',
                })
            }
        }
    }

    const totalSelected = isSelectAll
        ? (data?.meta?.total ?? 0) - excludeIds.length
        : undefined

    return (
        <ContentGuide>
            <OverlayApresentacao passos={passos} name="calculo" />
            <ContentGuide.Card>
                <ContentGuide.Header
                    label={['Manutenção do ponto', 'Cálculo do ponto']}
                >
                    <ContentGuide.Filtros
                        SelectEmpresas
                        SelectCentroCusto
                        SelectLocais
                    >
                        <SelectCompetencia
                            value={competencia}
                            onChange={setCompetencia}
                        />
                    </ContentGuide.Filtros>
                </ContentGuide.Header>
                <ContentGuide.DataGrid
                    key="grid-calculo"
                    serverSide
                    columns={columns}
                    rows={rows}
                    loading={isLoading}
                    checkboxSelection
                    onSelectionModelChange={handleSelectionChange}
                    selectionModel={selectionModel}
                    totalSelected={totalSelected}
                    totalRows={data?.meta?.total}
                    onPaginationChange={({ pageIndex, pageSize }) => {
                        setUrlState({
                            page: pageIndex + 1,
                            perPage: pageSize,
                        })
                    }}
                    defaultPageSize={perPage}
                    components={{
                        Toolbar: () => (
                            <GridMenuButton
                                startIcon={<UploadIcon />}
                                label={localeText.toolbarExport}
                                data-testid="botao_exportar-calculo"
                            >
                                <ExportarCsv
                                    label="Exportar todos como CSV"
                                    fileName="exportacao-calculo-ponto"
                                    mi
                                />
                                <BaixarCalculoButton
                                    competencia={competencia}
                                    formato="pdf"
                                    selectAll={isSelectAll}
                                    excludeIds={excludeIds}
                                    totalRows={data?.meta?.total ?? 0}
                                />
                                <BaixarCalculoButton
                                    competencia={competencia}
                                    formato="zip"
                                    selectAll={isSelectAll}
                                    excludeIds={excludeIds}
                                    totalRows={data?.meta?.total ?? 0}
                                />
                            </GridMenuButton>
                        ),
                    }}
                    onAdd={
                        <CalcularEmpresa
                            competencia={competencia}
                            funcionarioIds={selectedFuncionarioIds}
                        />
                    }
                    ToolbarProps={{ hideExportButton: true }}
                />
                <Outlet
                    key="outlet-calculo"
                    context={{ data: data?.data ?? [] }}
                />
            </ContentGuide.Card>
        </ContentGuide>
    )
}

export function useCalculoOutletContext() {
    return useOutletContext<{
        data: CalculoEspelho[]
    }>()
}
